import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  MAX_CHUNK_RELOADS,
  clearChunkReloadCount,
  incrementChunkReloadCount,
} from '@/lib/retryLazy';

function isChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('dynamically imported module')
  );
}

// React re-throws the same failing lazy chunks multiple times within a single
// page load, so getDerivedStateFromError can fire many times per refresh. These
// flags guarantee we only count (and decide to reload) ONCE per page load; a
// fresh page load re-evaluates this module, resetting them naturally.
let reloadCountedThisLoad = false;
let shouldReloadThisLoad = false;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  shouldReload: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, shouldReload: false };

  static getDerivedStateFromError(error: Error): State {
    if (isChunkError(error)) {
      // Only the FIRST call in this page load decides whether to reload; the
      // counter must climb 1->2->3 across refreshes, not 0->16 in one load.
      if (!reloadCountedThisLoad) {
        reloadCountedThisLoad = true;
        shouldReloadThisLoad = incrementChunkReloadCount() <= MAX_CHUNK_RELOADS;
      }
      // Issue the redirect from componentDidMount (never from render phase)
      // so it happens exactly once per committed fallback.
      return { hasError: true, error, shouldReload: shouldReloadThisLoad };
    }
    return { hasError: false, error: null, shouldReload: false };
  }

  componentDidMount() {
    if (this.state.hasError) {
      this.settleError();
    }
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (!prevState.hasError && this.state.hasError) {
      this.settleError();
    }
  }

  settleError() {
    if (this.state.shouldReload) {
      window.location.reload();
    } else {
      // We've shown the error screen (budget exhausted) — reset the stored
      // counter so the next failure incident gets a fresh set of retries
      // instead of being permanently poisoned by a previous run.
      clearChunkReloadCount();
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (!isChunkError(error)) {
      console.error(error, info);
    }
  }

  reloadAndClear = () => {
    clearChunkReloadCount();
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // While we're still retrying (budget not exhausted), reload immediately
      // and DON'T paint the error screen, so the user never sees it flash.
      if (this.state.shouldReload) {
        return null;
      }
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            padding: '24px',
            textAlign: 'center',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '12px' }}>
            Something went wrong
          </h1>
          <p style={{ color: '#666', marginBottom: '24px', maxWidth: '400px' }}>
            A critical part of the application failed to load after several retries. Please
            clear your browser cache and refresh the page.
          </p>
          <button
            onClick={this.reloadAndClear}
            style={{
              padding: '10px 24px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#fff',
              backgroundColor: '#000',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Clear cache &amp; reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}